import { createEditorStateWithText } from '@draft-js-plugins/editor';
import { EditorState } from 'draft-js';
import { Form, Formik } from 'formik';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { businessPartner, settings } from '../../../../../models/entities/businessPartner/businessPartner';
import { ePaymentTerms } from '../../../../../models/entities/businessPartner/paymentTerms';
import claimsType from '../../../../../models/entities/claimsType';
import { allowedCompany } from '../../../../../models/entities/user';
import { businessPartnersSelectors } from '../../../../../state/ducks/businessPartner/selectors';
import { localizationSelectors } from '../../../../../state/ducks/localization/selectors';
import { userSelectors } from '../../../../../state/ducks/user/selectors';
import { userDetails } from '../../../../../state/ducks/users/users';
import { RootState } from '../../../../../state/store/store';
import { claimsDomainsType } from '../../../../../userClaims';
import { emailPattern } from '../../../../../utils/patterns';
import { convertEditorStateToHtml, convertHtmlToEditorState } from '../../../../../utils/textEditorUtils';
import { enumToDropdownOptions, Option } from '../../../../shared/inputs/base/DropDown';
import TextEditor from '../../../../shared/inputs/base/TextEditor';
import { CurrencyInput, FormCheckbox, FormDropDown, FormTextInput, UserDropDownForm } from '../../../../shared/inputs/form';
import currencyList from '../../../../shared/inputs/form/CurrencyInput/currencyList';
import getSymbolFromCurrency from 'currency-symbol-map';
import { ModalFooter, ModalFormContainer } from '../../../../shared/Modal/Modal';
import Button from '../../../../shared/SmallComponents/Button';
import FieldLabel from '../../../../shared/SmallComponents/FieldLabel';
import Loader from '../../../../shared/SmallComponents/Loader';
import { styled } from '../../../../shared/Theme/theme';
import { formatBusinessPartnerApiError } from '../../utils/formatBusinessPartnerApiError';

const PROFIT_SURCHARGE_PERCENT_OPTIONS = Array.from({ length: 16 }, (_, i) => ({
    text: `${i * 0.5}%`,
    value: String(i * 0.5)
}));

function isValidProfitSurchargePercentInput(raw: string | undefined): boolean {
    if (raw === undefined || raw === null || raw === '') return true;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || n > 7.5) return false;
    return Math.abs(n * 2 - Math.round(n * 2)) < 1e-9;
}

function isValidVatInput(raw: string | undefined): boolean {
    if (raw === undefined || raw === null || raw === '') return true;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 && n <= 100;
}

export enum formMode {
    NEW,
    EDIT
}

interface formFields {
    name?: string;
    type?: string;
    address?: string;
    phone_number?: string;
    email?: string;
    contact_name?: string;
    vat_number?: string;
    website?: string;
    companyId?: string;
    paymentTerms?: Array<string>;
    paymentTermsRemarks?: string;
    taxesDuties?: boolean;
    remarksAlert?: boolean;
    monthlyIntrest?: number;
    creditLine?: number;
    creditLineCurrency: string;
    baseCurrency?: string;
    profitSurchargePercent?: string;
    vat?: string;
    remarks: EditorState;
    changestatealertvalues?: Array<string>;
    salesman?: Array<string>;
    operationsManager?: Array<string>;
}
const requiredFields = ['name', 'type', 'vat_number'];
type Props = {
    party?: businessPartner;
    type?: string;
    businessPartnerVatNumbers?: string[];
    mode: formMode;
    localizations?: any;
    settings?: settings;
    submitCallback?: (businessPartner: businessPartner) => void;
    openedFromModal?: {
        setVisible: React.Dispatch<React.SetStateAction<boolean>>;
    };
    confirmation?: { setConfirm: React.Dispatch<React.SetStateAction<boolean>> };
    createBusinessPartner: (party: businessPartner) => Promise<businessPartner>;
    updateBusinessPartner: (businessPartnerId: string, party: businessPartner) => Promise<number>;
    createError?: string;
    updateError?: string;
    clearErrors: () => void;
    allowedCompanies: allowedCompany[];
    getUserClaims: (domain: claimsDomainsType) => claimsType;
    usersInSameCompany?: Array<userDetails>;
};

export const partiesToDropdownOptions = (parties: Array<businessPartner> | undefined): Array<Option> => {
    if (!parties) return [];
    return parties.map((party) => ({
        text: party.name,
        value: '' + party.id
    }));
};

// Separate component to handle form content with proper hook usage
const FormContent = React.memo(({ 
    values, 
    setFieldValue, 
    dirty, 
    confirmation,
    localizations,
    allowedCompanies,
    partyTypesOptions,
    type,
    stateTypesOptions,
    usersInSameCompany,
    openedFromModal,
    mode,
    submitLoading,
    apiErrorDisplay
}: any) => {
    const currencyOptions = React.useMemo(
        () =>
            currencyList.data.map((c) => ({
                text: `${getSymbolFromCurrency(c.Ccy) || c.Ccy || c.CcyNm} — ${c.Ccy || c.CcyNm}`,
                value: c.Ccy || c.CcyNm
            })),
        []
    );

    // Handle confirmation state properly with useEffect
    React.useEffect(() => {
        if (confirmation && confirmation.setConfirm) {
            confirmation.setConfirm(dirty);
        }
    }, [dirty, confirmation]);

    const isCargoOwner = type === 'CARGO_OWNER' || values.type === 'CARGO_OWNER';
    React.useEffect(() => {
        if (isCargoOwner && !values.baseCurrency) {
            setFieldValue('baseCurrency', 'USD', false);
        }
    }, [isCargoOwner, values.baseCurrency, setFieldValue]);

    return (
        <Form>
            <Container>
                {allowedCompanies.length > 1 && (
                    <>
                        <FieldLabel>{localizations.parties.form.partycompany_placeholder}</FieldLabel>
                        <FieldContainer>
                            <FormDropDown
                                name="companyId"
                                placeHolder={localizations.parties.form.partycompany_placeholder}
                                options={allowedCompanies.map((item) => ({
                                    value: item.companyId,
                                    text: item.companyName
                                }))}
                            />
                        </FieldContainer>
                    </>
                )}
                <Flex>
                    <FieldContainer>
                        <FieldLabel>{localizations.parties.form.partyname_placeholder}</FieldLabel>
                        <FormTextInput name="name" placeHolder={localizations.parties.form.partyname_placeholder} />
                    </FieldContainer>

                    {!type && (
                        <>
                            <FieldContainer>
                                <FieldLabel>{localizations.parties.form.partytype_placeholder}</FieldLabel>
                                <FormDropDown
                                    name="type"
                                    options={partyTypesOptions || []}
                                    placeHolder={localizations.parties.form.partytype_placeholder}
                                    showSearch
                                />
                            </FieldContainer>
                        </>
                    )}
                </Flex>
                <Flex>
                    <FieldContainer>
                        <FieldLabel>{localizations.parties.form.partyvat_number_placeholder}</FieldLabel>
                        <FormTextInput name="vat_number" placeHolder={localizations.parties.form.partyvat_number_placeholder} />
                    </FieldContainer>
                    <FieldContainer>
                        <FieldLabel>{localizations.parties.form.partyphone_placeholder}</FieldLabel>
                        <FormTextInput name="phone_number" placeHolder={localizations.parties.form.partyphone_placeholder} />
                    </FieldContainer>
                </Flex>
                <Flex>
                    <FieldContainer>
                        <FieldLabel>{localizations.parties.form.partyemail_placeholder}</FieldLabel>
                        <FormTextInput name="email" placeHolder={localizations.parties.form.partyemail_placeholder} />
                    </FieldContainer>
                    <FieldContainer>
                        <FieldLabel>{localizations.parties.form.partywebsite_placeholder}</FieldLabel>
                        <FormTextInput name="website" placeHolder={localizations.parties.form.partywebsite_placeholder} />
                    </FieldContainer>
                </Flex>
                <Flex>
                    <FieldContainer>
                        <FieldLabel>{localizations.parties.form.payment_terms}</FieldLabel>
                        <FormDropDown
                            name="paymentTerms"
                            options={enumToDropdownOptions(ePaymentTerms)}
                            placeHolder={localizations.parties.form.payment_terms}
                            showSearch
                            mode="tags"
                            onChange={(arr: Array<string>) => {
                                if (arr.length > 0) setFieldValue('paymentTerms', [arr.pop()]);
                            }}
                        />
                    </FieldContainer>
                    <FieldContainer>
                        <FieldLabel>{localizations.parties.form.payment_terms_remarks}</FieldLabel>
                        <FormTextInput name="paymentTermsRemarks" placeHolder={localizations.parties.form.payment_terms_remarks} />
                    </FieldContainer>
                </Flex>
                <Flex>
                    <FieldContainer>
                        <FieldLabel>{localizations.parties.form.credit_line}</FieldLabel>
                        <CurrencyInput
                            currencyValue={values.creditLineCurrency}
                            numberInputName="creditLine"
                            numberPlaceholder={localizations.parties.form.credit_line}
                            unitInputName="creditLineCurrency"
                        />
                    </FieldContainer>

                    <FieldContainer>
                        <FieldLabel>{localizations.parties.form.monthly_intrest}</FieldLabel>
                        <FormTextInput
                            name="monthlyIntrest"
                            placeHolder={localizations.parties.form.monthly_intrest}
                            type="number"
                            min="0"
                            step="0.01"
                        />
                    </FieldContainer>
                </Flex>
                <Flex>
                    {!type && (
                        <>
                            <FieldContainer>
                                <FieldLabel>{localizations.parties.form.updatestates_placeholder}</FieldLabel>
                                <FormDropDown
                                    name="changestatealertvalues"
                                    mode="multiple"
                                    options={stateTypesOptions || []}
                                    placeHolder={localizations.parties.form.updatestates_placeholder}
                                    showSearch
                                />
                            </FieldContainer>
                            <FieldContainer>
                                <FieldLabel>{localizations.parties.form.salesman_placeholder}</FieldLabel>
                                <UserDropDownForm
                                    name="salesman"
                                    placeHolder={localizations.parties.form.salesman_placeholder}
                                    users={usersInSameCompany}
                                    customUsers
                                    mode="multiple"
                                    showSearch
                                />
                            </FieldContainer>
                        </>
                    )}
                </Flex>
                {!type && (
                    <Flex>
                        <FieldContainer>
                            <FieldLabel>{localizations.parties.form.operations_manager_placeholder}</FieldLabel>
                            <UserDropDownForm
                                name="operationsManager"
                                placeHolder={localizations.parties.form.operations_manager_placeholder}
                                users={usersInSameCompany}
                                customUsers
                                mode="multiple"
                                showSearch
                            />
                        </FieldContainer>
                    </Flex>
                )}
                {isCargoOwner && (
                    <Flex>
                        <FieldContainer>
                            <FieldLabel>{localizations.parties.form.base_currency}</FieldLabel>
                            <FormDropDown
                                name="baseCurrency"
                                options={currencyOptions}
                                placeHolder={localizations.parties.form.base_currency_placeholder}
                                showSearch
                                allowClear={false}
                            />
                        </FieldContainer>
                        <FieldContainer>
                            <FieldLabel>{localizations.parties.form.profit_surcharge_percent}</FieldLabel>
                            <FormDropDown
                                name="profitSurchargePercent"
                                options={PROFIT_SURCHARGE_PERCENT_OPTIONS}
                                placeHolder={localizations.parties.form.profit_surcharge_percent_placeholder}
                                showSearch
                                allowClear
                            />
                        </FieldContainer>
                    </Flex>
                )}
                {isCargoOwner && (
                    <Flex>
                        <FieldContainer>
                            <FieldLabel>{localizations.parties.form.vat}</FieldLabel>
                            <FormTextInput
                                name="vat"
                                placeHolder={localizations.parties.form.vat_placeholder}
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                            />
                        </FieldContainer>
                    </Flex>
                )}
                <FieldContainer>
                    <div style={{ display: 'flex' }}>
                        <FieldLabel>{localizations.parties.form.remarks_placeholder}</FieldLabel>
                        <FormCheckbox style={{ marginLeft: '100px' }} name="remarksAlert">
                            {localizations.parties.form.remarks_alert}
                        </FormCheckbox>
                    </div>
                    <TextEditor
                        id="remarks-text-editor"
                        editorState={values.remarks}
                        onChange={(state: EditorState) => {
                            setFieldValue('remarks', state);
                        }}
                    />
                </FieldContainer>
                <FieldContainer>
                    <FormCheckbox name="taxesDuties">{localizations.parties.form.taxes_duties}</FormCheckbox>
                </FieldContainer>
                {apiErrorDisplay ? <Error>{apiErrorDisplay}</Error> : null}
            </Container>
            {openedFromModal && (
                <FormFooter>
                    <Button minWidth="55px" onClick={() => openedFromModal.setVisible(false)}>
                        <span>{localizations.parties.modal.cancel_btn}</span>
                    </Button>
                    <Button minWidth="55px" buttonType="filled" type="submit">
                        {submitLoading ? (
                            <Loader width="20px" marginTop="0px" showText={false} />
                        ) : (
                            <span>
                                {mode === formMode.NEW ? localizations.parties.modal.add_btn : localizations.parties.modal.edit_btn}
                            </span>
                        )}
                    </Button>
                </FormFooter>
            )}
        </Form>
    );
});

const BusinessPartnerFormConnected = ({
    party,
    type,
    localizations,
    mode,
    openedFromModal,
    businessPartnerVatNumbers,
    createBusinessPartner,
    updateBusinessPartner,
    confirmation,
    submitCallback,
    createError,
    updateError,
    settings,
    allowedCompanies,
    getUserClaims,
    usersInSameCompany,
    clearErrors
}: Props) => {
    const [submitLoading, setLoading] = useState(false);
    const emptyEditorState = useRef(createEditorStateWithText(''));
    const previousModalStateRef = useRef<boolean>(false);

    // Clear errors only when modal opens or closes
    React.useEffect(() => {
        const isModalOpen = !!openedFromModal;
        const wasModalOpen = previousModalStateRef.current;
        
        // Clear errors when modal state changes (opens or closes)
        if (isModalOpen !== wasModalOpen) {
            clearErrors();
        }
        
        previousModalStateRef.current = isModalOpen;
    }, [openedFromModal, clearErrors]);

    const partyTypesOptions = useMemo(() => {
        if (!getUserClaims('admin').view) {
            return settings?.partyTypes
                ?.map((item) => ({
                    text: item.name,
                    value: item.code
                }))
                .filter((data) => data.text != 'Cargo Owner');
        }
        return settings?.partyTypes?.map((item) => ({
            text: item.name,
            value: item.code
        }));
    }, [settings?.partyTypes, getUserClaims]);

    const stateTypesOptions = useMemo(() => 
        settings?.state?.map((item) => ({
            text: item.name,
            value: item.code
        })), [settings?.state]);
    const initFormValues: formFields = {
        name: '',
        type: '',
        address: '',
        phone_number: '',
        email: '',
        vat_number: '',
        website: '',
        companyId: '',
        paymentTerms: [],
        paymentTermsRemarks: '',
        taxesDuties: false,
        remarksAlert: false,
        monthlyIntrest: 0,
        creditLine: 0,
        creditLineCurrency: 'USD',
        baseCurrency: '',
        profitSurchargePercent: '',
        vat: '',
        remarks: emptyEditorState.current,
        changestatealertvalues: [],
        salesman: [],
        operationsManager: []
    };
    const setInitialValues = () => {
        if (!party || mode === formMode.NEW) {
            initFormValues.type = type;
            if (type === 'CARGO_OWNER') {
                initFormValues.baseCurrency = 'USD';
            }
            return initFormValues;
        } else {
            initFormValues.name = party.name;
            initFormValues.type = party.type;
            initFormValues.phone_number = party.phoneNumber;
            initFormValues.email = party.email;
            initFormValues.vat_number = party.vatNumber;
            initFormValues.website = party.website || '';
            initFormValues.companyId = party.companyId;
            initFormValues.paymentTerms = party.paymentTerms ? [party.paymentTerms] : undefined;
            initFormValues.paymentTermsRemarks = party.paymentTermsRemarks;
            initFormValues.taxesDuties = party.taxesDuties;
            initFormValues.remarksAlert = party.remarksAlert;
            initFormValues.monthlyIntrest = party.monthlyIntrest;
            initFormValues.creditLine = party.creditLine;
            initFormValues.creditLineCurrency = party.creditLineCurrency;
            initFormValues.baseCurrency =
                party.type === 'CARGO_OWNER' ? party.baseCurrency || 'USD' : party.baseCurrency || '';
            initFormValues.profitSurchargePercent =
                party.profitSurchargePercent !== null && party.profitSurchargePercent !== undefined
                    ? String(party.profitSurchargePercent)
                    : '';
            initFormValues.vat = party.vat !== null && party.vat !== undefined ? String(party.vat) : '';
            initFormValues.remarks = convertHtmlToEditorState(
                party.remarks,
                (id: string) => undefined,
                (id: string) => undefined,
                undefined
            );
            initFormValues.changestatealertvalues = party.changestatealertvalues?.split(',');
            initFormValues.salesman = party.salesman?.split(',');
            initFormValues.operationsManager = party.operationsManager?.split(',');
            return initFormValues;
        }
    };
    const validate = useCallback((values: any) => {
        const errors: any = {};
        
        // Skip validation for fields that don't need it on every keystroke
        const { remarks, ...valuesToValidate } = values;
        
        //TODO : add validation of vat number.(unique per company id)
        requiredFields.forEach((field) => {
            if (!valuesToValidate[field]) errors[field] = localizations.parties.form.required_error;
        });
        const isCargoOwner = type === 'CARGO_OWNER' || values.type === 'CARGO_OWNER';
        if (isCargoOwner && !valuesToValidate.baseCurrency) {
            errors.baseCurrency = localizations.parties.form.required_error;
        }
        if (isCargoOwner && values.profitSurchargePercent && !isValidProfitSurchargePercentInput(String(values.profitSurchargePercent))) {
            errors.profitSurchargePercent = localizations.parties.form.required_error;
        }
        if (isCargoOwner && values.vat && !isValidVatInput(String(values.vat))) {
            errors.vat = localizations.parties.form.required_error;
        }
        //if (values.phone_number && !values.phone_number.match(phonePattern)) errors.phone_number = localizations.parties.form.phone_error;
        if (valuesToValidate.email && !valuesToValidate.email.match(emailPattern)) errors.email = localizations.parties.form.email_error;

        const maxLengthValidation = (field: string, maxLength: number, message: string) => {
            const value = valuesToValidate[field];
            if (Array.isArray(value) && value.length > 0) {
                if (value[0] && value[0].length > maxLength) errors[field] = message;
            } else if (value && typeof value === 'string' && value.length > maxLength) {
                errors[field] = message;
            }
        };

        let vatNumbers = businessPartnerVatNumbers;

        if (party && vatNumbers) {
            const currentVatIndex = vatNumbers.indexOf(party.vatNumber);
            if (currentVatIndex !== -1) {
                vatNumbers = [...vatNumbers];
                vatNumbers.splice(currentVatIndex, 1);
            }
        }

        if (vatNumbers?.includes(valuesToValidate.vat_number)) errors.vat_number = localizations.parties.form.vat_number_error;

        maxLengthValidation('name', 100, localizations.parties.form.name_max_length_error);
        maxLengthValidation('vat_number', 20, localizations.parties.form.vat_number_max_length_error);
        maxLengthValidation('phone_number', 40, localizations.parties.form.phone_max_length_error);
        maxLengthValidation('email', 100, localizations.parties.form.email_max_length_error);
        maxLengthValidation('website', 50, localizations.parties.form.website_max_length_error);
        maxLengthValidation('paymentTerms', 25, localizations.parties.form.payment_terms_max_length_error);
        maxLengthValidation('paymentTermsRemarks', 150, localizations.parties.form.payment_terms_remarks_max_length_error);

        return errors;
    }, [businessPartnerVatNumbers, party, localizations, mode, type]);
    const submit = (values: any) => {
        const isCargoOwner = type === 'CARGO_OWNER' || values.type === 'CARGO_OWNER';
        const payload = {
            name: values.name,
            type: values.type,
            phoneNumber: values.phone_number,
            email: values.email,
            vatNumber: values.vat_number,
            website: values.website,
            companyId: values.companyId,
            paymentTerms: values.paymentTerms?.length > 0 ? values.paymentTerms[0] : null,
            paymentTermsRemarks: values.paymentTermsRemarks,
            taxesDuties: values.taxesDuties,
            remarksAlert: values.remarksAlert,
            monthlyIntrest: Number(values.monthlyIntrest),
            creditLine: Number(values.creditLine),
            creditLineCurrency: values.creditLineCurrency,
            baseCurrency: isCargoOwner ? values.baseCurrency || null : null,
            profitSurchargePercent:
                values.profitSurchargePercent === '' || values.profitSurchargePercent === undefined || values.profitSurchargePercent === null
                    ? null
                    : Number(values.profitSurchargePercent),
            vat: values.vat === '' || values.vat === undefined || values.vat === null ? null : Number(values.vat),
            remarks: convertEditorStateToHtml(values.remarks),
            changestatealertvalues: values.changestatealertvalues?.toString(),
            salesman: values.salesman?.toString(),
            operationsManager: values.operationsManager?.toString()
        } as businessPartner;
        if (mode === formMode.NEW) {
            setLoading(true);
            createBusinessPartner(payload)
                .then((value: businessPartner) => {
                    if (submitCallback) submitCallback(value);
                    if (openedFromModal && value) {
                        openedFromModal.setVisible(false);
                    } else {
                        setLoading(false);
                    }
                })
                .catch((err) => {
                    setLoading(false);
                });
        } else if (mode === formMode.EDIT && party) {
            setLoading(true);
            updateBusinessPartner(party.id, payload)
                .then((success: number) => {
                    if (submitCallback) submitCallback(payload);
                    if (openedFromModal && success) {
                        openedFromModal.setVisible(false);
                    } else {
                        setLoading(false);
                    }
                })
                .catch(() => {
                    setLoading(false);
                });
        }
    };
    const initialValues = useMemo(() => setInitialValues(), [party, mode, type]);

    const apiErrorDisplay = useMemo(() => {
        const byCode: Record<string, string | undefined> = {
            CARGO_OWNER_BASE_CURRENCY_REQUIRED: localizations?.parties?.form?.error_cargo_owner_base_currency_required
        };
        return (
            formatBusinessPartnerApiError(createError, byCode) || formatBusinessPartnerApiError(updateError, byCode)
        );
    }, [createError, updateError, localizations]);

    return (
        <Formik 
            initialValues={initialValues} 
            validate={validate} 
            onSubmit={submit} 
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={true}>
            {({ values, setFieldValue, dirty, isValid }) => {
                return (
                    <FormContent
                        values={values}
                        setFieldValue={setFieldValue}
                        dirty={dirty}
                        confirmation={confirmation}
                        localizations={localizations}
                        allowedCompanies={allowedCompanies}
                        partyTypesOptions={partyTypesOptions}
                        type={type}
                        stateTypesOptions={stateTypesOptions}
                        usersInSameCompany={usersInSameCompany}
                        openedFromModal={openedFromModal}
                        mode={mode}
                        submitLoading={submitLoading}
                        apiErrorDisplay={apiErrorDisplay}
                    />
                );
            }}
        </Formik>
    );
};
const FieldContainer = styled.div`
    margin-bottom: 12px;
`;

const Container = styled(ModalFormContainer)``;

const FormFooter = styled(ModalFooter)`
    position: absolute;
    bottom: 0;
    width: 100%;
    left: 0;
    right: 0;
    background: white;
`;
const Error = styled.div`
    width: 100%;
    max-width: 100%;
    color: #ff4d4f;
    padding: 8px 12px;
    margin-top: 8px;
    background-color: #fff2f0;
    border: 1px solid #ffccc7;
    border-radius: 4px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: 14px;
    line-height: 1.5;
`;

const Flex = styled.div`
    display: flex;
    > * {
        margin-right: 10px;
        flex-basis: 50%;
    }
`;

const mapStateToProps = (state: RootState) => ({
    localizations: localizationSelectors.singleShipmentPage(state),
    createError: state.error.effects.businessPartners.createBusinessPartner,
    updateError: state.error.effects.businessPartners.updateBusinessPartner,
    settings: businessPartnersSelectors.settingsByLanguage(state),
    businessPartnerVatNumbers: businessPartnersSelectors.businessPartnerVatNumbers(state),
    allowedCompanies: userSelectors.allowedCompanies(state),
    getUserClaims: (domain: claimsDomainsType) => userSelectors.getUserClaims(state)(domain),
    usersInSameCompany: userSelectors.usersInSameCompany(state)
});

const mapDispatchToProps = (dispatch: any) => ({
    createBusinessPartner: async (party: businessPartner) => dispatch.businessPartners.createBusinessPartner(party),
    updateBusinessPartner: async (businessPartnerId: string, party: businessPartner) =>
        dispatch.businessPartners.updateBusinessPartner({ businessPartnerId, party }),
    clearErrors: () => {
        dispatch.error.hide({ name: 'businessPartners', action: 'createBusinessPartner' });
        dispatch.error.hide({ name: 'businessPartners', action: 'updateBusinessPartner' });
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(BusinessPartnerFormConnected));
